const BYO_VIEW_TYPE_LABELS = Object.freeze({
    ortho_view: 'Ortho package',
    cesium_tiles_view: '3D Tiles',
    point_cloud_view: 'Point cloud',
    gaussian_splat_view: 'Gaussian Splat'
});
const ACTIVE_BILLING_STATUSES = Object.freeze([
    'active',
    'trialing',
    'paid',
    'manual_approved'
]);
const PENDING_BILLING_STATUSES = Object.freeze([
    '',
    'inactive',
    'pending',
    'invoice_pending'
]);

function getByoHosting(plan = {}) {
    return plan?.byo_hosting || {};
}

function getByoLimits(plan = {}) {
    return getByoHosting(plan).limits || {};
}

function pluraliseDataset(count) {
    return count === 1 ? 'dataset' : 'datasets';
}

export function isByoHostingPlan(plan = {}) {
    return Boolean(getByoHosting(plan).is_byo_hosting_plan);
}

export function formatByoStorageLimit(plan = {}) {
    if (!isByoHostingPlan(plan)) return 'Not included';

    const storageGb = Number(getByoLimits(plan).included_storage_gb);
    if (storageGb === -1) return 'Unlimited';
    if (!storageGb || Number.isNaN(storageGb)) return 'Not configured';

    if (storageGb >= 1024 && storageGb % 1024 === 0) {
        return `${storageGb / 1024} TB`;
    }

    return `${storageGb.toLocaleString()} GB`;
}

export function formatByoHostedDatasetLimit(plan = {}) {
    if (!isByoHostingPlan(plan)) return 'Not included';

    const datasetLimit = Number(getByoLimits(plan).included_hosted_datasets);
    if (datasetLimit === -1) return 'Unlimited';
    if (!datasetLimit || Number.isNaN(datasetLimit)) return 'Not configured';

    return `${datasetLimit.toLocaleString()} hosted ${pluraliseDataset(datasetLimit)}`;
}

export function formatByoSupportedViewTypes(plan = {}) {
    if (!isByoHostingPlan(plan)) return 'Not included';

    const supportedViewTypes = getByoHosting(plan).supported_view_types || [];
    return formatByoViewTypeList(supportedViewTypes) || 'Not configured';
}

export function formatByoViewTypeList(viewTypes = []) {
    if (!Array.isArray(viewTypes) || viewTypes.length === 0) return '';
    return viewTypes
        .map((viewType) => BYO_VIEW_TYPE_LABELS[viewType] || viewType)
        .join(', ');
}

export function formatByoBillingMode(plan = {}) {
    if (!isByoHostingPlan(plan)) return 'Not included';

    const byoHosting = getByoHosting(plan);
    const stripe = byoHosting.stripe || {};
    if (stripe.mapping_mode === 'stripe_subscription') {
        const frequency = Number(
            plan?.frequency ?? byoHosting?.pricing?.frequency_months
        );
        if (frequency === 12) return 'Stripe annual subscription';
        if (frequency === 3) return 'Stripe quarterly subscription';
        return 'Stripe subscription';
    }

    const manualInvoiceSku =
        byoHosting.manual_invoice_sku || stripe.manual_invoice_sku || '';

    return manualInvoiceSku
        ? `Manual invoice (${manualInvoiceSku})`
        : 'Manual invoice';
}

export function formatByoBillingStatus(plan = {}) {
    if (!isByoHostingPlan(plan)) return 'Not included';

    const billingAccount = plan.billing_account || {};
    const subscriptionStatus = String(
        billingAccount.subscription_status || ''
    ).toLowerCase();

    if (billingAccount.skip_validation) {
        return 'Manual invoice approved';
    }

    if (
        billingAccount.active ||
        ACTIVE_BILLING_STATUSES.includes(subscriptionStatus)
    ) {
        return 'Active';
    }

    if (PENDING_BILLING_STATUSES.includes(subscriptionStatus)) {
        return 'Activation pending';
    }

    return `Not active (${subscriptionStatus.replace(/_/g, ' ')})`;
}

export function formatByoHostedDatasetUsage(plan = {}) {
    if (!isByoHostingPlan(plan)) return 'Not included';

    const byoHosting = getByoHosting(plan);
    const activeHostedDatasets = Number(
        byoHosting?.usage?.active_hosted_datasets || 0
    );
    const datasetLimit = Number(getByoLimits(plan).included_hosted_datasets);
    const formattedUsage = activeHostedDatasets.toLocaleString();

    if (datasetLimit === -1) {
        return `${formattedUsage} active hosted datasets; unlimited allowance`;
    }

    if (!datasetLimit || Number.isNaN(datasetLimit)) {
        return `${formattedUsage} active hosted datasets; allowance not configured`;
    }

    return `${formattedUsage} of ${datasetLimit.toLocaleString()} hosted ${pluraliseDataset(datasetLimit)} used`;
}

export function getByoPlanSummary(plan = {}) {
    if (!isByoHostingPlan(plan)) return '';

    return `${formatByoStorageLimit(plan)} included, ${formatByoHostedDatasetLimit(
        plan
    )}.`;
}
